import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import { ArticlesType, mapArticleTypeToTitle } from '@/creditflowresearch/ui/components/creditFlow/articles/constants';
import { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { ITheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Article';
import { ThemeType } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/themeTypes';
import { IFilterGroupConfig } from './FilterGroupList';
import type { IFilteredThemesResult } from './adapters/types';

export const getGroupedThemesIds = (groupedThemes: Record<string, ITheme[]>) => Object.entries(groupedThemes).reduce(
    (acc, [type, themes]) => ({ ...acc, [type]: themes.map((theme) => theme.id) }), {},
);

export const getThemesChildren = (themes: ITheme[]) => themes.map((theme) => ({
  value: theme.id,
  label: theme.themeName,
  disabled: false,
}));

export const filterAndGroupThemes = (
  themes: IMarketTheme[],
  predicate: (theme: IMarketTheme) => boolean,
): IFilteredThemesResult => {
  const filteredThemes = themes.filter(predicate);
  const groupedThemes = groupBy(filteredThemes, 'type');

  return {
    groupedThemes,
    themesIds: getGroupedThemesIds(groupedThemes),
  };
};

export const buildBaseFilterGroup = (config: {
  key: ThemeType,
  articleTypeTitle: string,
  groupedThemes: Record<string, ITheme[]>,
  themesIds: Record<string, string[]>,
  groupTitle: string,
}): IFilterGroupConfig<ThemeType> => {
  const themes = config.groupedThemes[config.articleTypeTitle] || [];
  const items = getThemesChildren(themes);
  const itemIds = config.themesIds[config.articleTypeTitle] || [];

  return {
    key: config.key,
    groupTitle: config.groupTitle,
    filterByChildrenName: ArticlesType[config.key],
    nestedIds: itemIds,
    children: items,
    shouldRender: items.length > 0,
  };
};

export const buildBaseFilterGroups = (
  groupedThemes: Record<string, ITheme[]>,
  themesIds: Record<string, string[]>,
  titleGenerator: (articleTypeTitle: string) => string,
): IFilterGroupConfig<ThemeType>[] => {
  return Object.keys(ArticlesType).map((key: ThemeType) => {
    const articleTypeTitle = mapArticleTypeToTitle[key];
    const groupTitle = titleGenerator(articleTypeTitle);

    return buildBaseFilterGroup({
      key,
      articleTypeTitle,
      groupedThemes,
      themesIds,
      groupTitle,
    });
  });
};

const getInitialThemes = (
    ids: string[] = [],
    themesSubscriptionsIds: string[] = [],
) => ids.filter((id) => themesSubscriptionsIds.includes(id));

export const getInitialThemesState = (
    themesIds: Record<typeof mapArticleTypeToTitle[ThemeType], string[]>,
    themesSubscriptionsIds: string[] = [],
) => ({
  [ThemeType.dealFlow]:
    getInitialThemes(themesIds[mapArticleTypeToTitle[ThemeType.dealFlow]], themesSubscriptionsIds) || [],
  [ThemeType.commentary]:
    getInitialThemes(themesIds[mapArticleTypeToTitle[ThemeType.commentary]], themesSubscriptionsIds) || [],
  includeAllType: [],
});

export const getSubscribeThemeIds = (values: ReturnType<typeof getInitialThemesState>, {
  themes,
  themesSubscriptionIds,
  currentRegionIds,
  currentProductId,
}: {
  themes: IMarketTheme[],
  themesSubscriptionIds: string[],
  currentRegionIds: string[],
  currentProductId?: string,
}): string[] => {
  const themesById = new Map(themes.map((t) => [t.id, t]));
  const formThemeIds = Object.values(omit(values, 'includeAllType')).flat();
  const currentRegionIdsSet = new Set(currentRegionIds);

  const themesToKeep = themesSubscriptionIds.filter((themeId) => {
    const theme = themesById.get(themeId);
    if (!theme) return true;

    if (currentProductId) {
      return !currentRegionIdsSet.has(theme.regionId) || theme.productId !== currentProductId;
    }

    return !currentRegionIdsSet.has(theme.regionId);
  });

  return [...formThemeIds, ...themesToKeep];
};
